import { useCallback, useMemo } from 'react';
import { GarantieDeriveeBrancheDto } from 'api/indice';
import { branchesAPI } from 'services/swagger-api';
import { useAsyncResource } from 'back-office/shared/hooks/useAsyncResource';
import { DerivedPremiumRates } from './guaranteeLabels';

export interface Branch extends DerivedPremiumRates {
  id: string;
  code: string;
  label: string;
  /** Primes dérivées (Cat Nat, Cat Tech, Attentat) calculées sur cette branche. */
  derivedPremiumsEnabled: boolean;
  /** Garanties de la branche qui portent un rôle dérivé, au plus une par rôle. */
  derivedGuarantees: GarantieDeriveeBrancheDto[];
}

/**
 * Branches du référentiel, triées par libellé.
 *
 * Passe par `referentiel/branches`, qui renvoie l'intégralité des branches. Les autres
 * sources en donnent une vue partielle : `risques/branches-courtier` part des tunnels
 * simulables par le courtier connecté — juste pour la souscription, mais trompeur ici,
 * où un administrateur doit pouvoir désigner une branche même sans y avoir de tunnel.
 */
export const useBranchesResource = () => {
  const fetchBranches = useCallback(() => branchesAPI.getReferentialBranches(), []);
  const resource = useAsyncResource(fetchBranches);

  const branches = useMemo<Branch[]>(
    () =>
      (resource.data ?? [])
        .map((item) => ({
          id: item.id,
          code: item.code ?? '',
          label: item.libelle || item.code || 'Branche sans libellé',
          derivedPremiumsEnabled: item.derivedPremiumsEnabled,
          catNatRate: item.catNatRate,
          catTechRate: item.catTechRate,
          attackRate: item.attackRate,
          derivedGuarantees: item.derivedGuarantees ?? [],
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'fr')),
    [resource.data]
  );

  return { ...resource, branches };
};

/** Raccourci lorsque seul le tableau importe. */
export const useBranches = (): Branch[] => useBranchesResource().branches;

/** Retrouve une branche par son identifiant. */
export const useBranch = (brancheId?: string): Branch | undefined => {
  const branches = useBranches();
  return useMemo(
    () => branches.find((branch) => branch.id === brancheId),
    [branches, brancheId]
  );
};
